export class Point {
    constructor(x, y) {
        this.x = x;
        this.y = y;
    }
}

export class MovingPoint {
    constructor(config) {
        const {
            canvasWidth,
            canvasHeight,
            radius,
            color,
            minOpacity,
            maxOpacity,
            minSpeed,
            maxSpeed,
            cellSize,
            numRows,
            movingPoints
        } = config;

        this.canvasWidth = canvasWidth;
        this.canvasHeight = canvasHeight;
        this.radius = radius;
        this.color = color;
        this.minOpacity = minOpacity;
        this.maxOpacity = maxOpacity;
        this.speed = (Math.random() * (maxSpeed - minSpeed) + minSpeed) * 60;
        this.cellSize = cellSize;
        this.numRows = numRows;
        this.movingPoints = movingPoints;
        this.numPoints = Math.floor(canvasWidth / cellSize);

        this.opacity = Math.random() * (maxOpacity - minOpacity) + minOpacity;
        this.chooseNewCoordinates();
    }

    checkIfLineOccupied(index, direction) {
        return this.movingPoints?.some(point =>
            point.lineIndex === index && point.direction === direction
        ) ?? false;
    }

    chooseNewCoordinates() {
        this.direction = Math.random() < 0.5 ? 'horizontal' : 'vertical';
        let lineIndex;
        let attempts = 10;

        do {
            lineIndex = Math.floor(Math.random() *
                (this.direction === 'horizontal' ? this.numRows : this.numPoints));
            attempts--;
        } while (this.checkIfLineOccupied(lineIndex, this.direction) && attempts > 0);

        this.lineIndex = lineIndex;
        const coordinate = this.lineIndex * this.cellSize;

        if (this.direction === 'horizontal') {
            this.x = -this.radius;
            this.y = coordinate;
        } else {
            this.x = coordinate;
            this.y = -this.radius;
        }
    }

    update(deltaTime, skipArea) {
        // Update position
        if (this.direction === 'horizontal') {
            this.x += this.speed * deltaTime;
            if (this.x > this.canvasWidth + this.radius) {
                this.chooseNewCoordinates();
            }
        } else {
            this.y += this.speed * deltaTime;
            if (this.y > this.canvasHeight + this.radius) {
                this.chooseNewCoordinates();
            }
        }

        // Calculate distance from skip area edges
        const edgeBuffer = 50; // Size of fade zone in pixels

        // Calculate distances from all edges
        const distanceFromLeft = Math.abs(this.x - skipArea.x);
        const distanceFromRight = Math.abs(this.x - (skipArea.x + skipArea.width));
        const distanceFromTop = Math.abs(this.y - skipArea.y);
        const distanceFromBottom = Math.abs(this.y - (skipArea.y + skipArea.height));

        // Check if point is inside or near skip area
        const isInHorizontalRange = this.x >= skipArea.x - edgeBuffer &&
            this.x <= skipArea.x + skipArea.width + edgeBuffer;
        const isInVerticalRange = this.y >= skipArea.y - edgeBuffer &&
            this.y <= skipArea.y + skipArea.height + edgeBuffer;

        if (isInHorizontalRange && isInVerticalRange) {
            // Inside or near skip area
            if (this.x >= skipArea.x &&
                this.x <= skipArea.x + skipArea.width &&
                this.y >= skipArea.y &&
                this.y <= skipArea.y + skipArea.height) {
                // Completely inside skip area
                this.opacity = 0;
            } else {
                // In the edge buffer zone - calculate fade
                let minDistance;

                if (this.x < skipArea.x) {
                    minDistance = distanceFromLeft;
                } else if (this.x > skipArea.x + skipArea.width) {
                    minDistance = distanceFromRight;
                } else if (this.y < skipArea.y) {
                    minDistance = distanceFromTop;
                } else {
                    minDistance = distanceFromBottom;
                }

                // Smooth transition using ease function
                const progress = Math.min(minDistance / edgeBuffer, 1);
                this.opacity = this.easeInOutCubic(progress);
            }
        } else {
            // Outside skip area and buffer zone
            this.opacity = 1;
        }
    }

    draw(ctx) {
        const { x, y } = this;
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.arc(x, y, this.radius, 0, 2 * Math.PI);
        ctx.fillStyle = `rgba(${this.color}, ${this.opacity})`;
        ctx.fill();
    }

    easeInOutCubic(x) {
        return x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2;
    }
}

export class MovingArrow {
    constructor(config) {
        const {
            canvasWidth,
            canvasHeight,
            arrowSize,
            color,
            minOpacity,
            maxOpacity,
            minSpeed,
            maxSpeed,
            cellSize,
            movingArrows
        } = config;

        this.canvasWidth = canvasWidth;
        this.canvasHeight = canvasHeight;
        this.arrowSize = arrowSize;
        this.color = color;
        this.minOpacity = minOpacity;
        this.maxOpacity = maxOpacity;
        this.speed = (Math.random() * (maxSpeed - minSpeed) + minSpeed) * 60;
        this.cellSize = cellSize;
        this.movingArrows = movingArrows;
        this.numPoints = Math.floor(canvasWidth / cellSize);

        this.opacity = Math.random() * (maxOpacity - minOpacity) + minOpacity;
        this.chooseNewCoordinates();
    }

    checkIfLineOccupied(index) {
        return this.movingArrows?.some(arrow => arrow.lineIndex === index) ?? false;
    }

    chooseNewCoordinates() {
        let attempts = 10;
        do {
            this.lineIndex = Math.floor(Math.random() * this.numPoints);
            attempts--;
        } while (this.checkIfLineOccupied(this.lineIndex) && attempts > 0);

        this.x = this.lineIndex * this.cellSize;
        this.y = this.canvasHeight + this.arrowSize;
    }

    update(deltaTime, skipArea) {
        // Update position
        this.y -= this.speed * deltaTime;
        if (this.y < -this.arrowSize) {
            this.chooseNewCoordinates();
        }

        // Calculate fade effect
        const edgeBuffer = 50; // Size of fade zone in pixels

        const isInHorizontalRange = this.x >= skipArea.x - edgeBuffer &&
            this.x <= skipArea.x + skipArea.width + edgeBuffer;
        const isInVerticalRange = this.y >= skipArea.y - edgeBuffer &&
            this.y <= skipArea.y + skipArea.height + edgeBuffer;

        if (isInHorizontalRange && isInVerticalRange) {
            // Check if arrow is completely inside skip area
            if (this.x >= skipArea.x &&
                this.x <= skipArea.x + skipArea.width &&
                this.y >= skipArea.y &&
                this.y <= skipArea.y + skipArea.height) {
                // Inside skip area - fully transparent
                this.opacity = 0;
            } else {
                // Calculate the minimum distance to the nearest edge
                let minDistance;

                // Calculate distances to each edge
                const distanceFromLeft = Math.abs(this.x - skipArea.x);
                const distanceFromRight = Math.abs(this.x - (skipArea.x + skipArea.width));
                const distanceFromTop = Math.abs(this.y - skipArea.y);
                const distanceFromBottom = Math.abs(this.y - (skipArea.y + skipArea.height));

                if (this.x < skipArea.x) {
                    minDistance = distanceFromLeft;
                } else if (this.x > skipArea.x + skipArea.width) {
                    minDistance = distanceFromRight;
                } else if (this.y < skipArea.y) {
                    minDistance = distanceFromTop;
                } else {
                    minDistance = distanceFromBottom;
                }

                // Calculate smooth opacity transition
                const progress = Math.min(minDistance / edgeBuffer, 1);
                this.opacity = this.easeInOutCubic(progress);
            }
        } else {
            // Outside skip area and buffer zone - fully visible
            this.opacity = 1;
        }
    }

    draw(ctx) {
        ctx.beginPath();
        ctx.moveTo(this.x, this.y);
        ctx.lineTo(this.x - this.arrowSize / 2.5, this.y + this.arrowSize / 2);
        ctx.lineTo(this.x + this.arrowSize / 2.5, this.y + this.arrowSize / 2);
        ctx.closePath();

        ctx.moveTo(this.x, this.y + this.arrowSize / 2);
        ctx.lineTo(this.x - this.arrowSize / 2.5, this.y + this.arrowSize);
        ctx.lineTo(this.x + this.arrowSize / 2.5, this.y + this.arrowSize);
        ctx.closePath();

        ctx.fillStyle = `rgba(${this.color}, ${this.opacity})`;
        ctx.fill();
    }

    easeInOutCubic(x) {
        return x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2;
    }
}
